import { useQuery } from "@tanstack/react-query";
import { reportsApi } from "@/api/client";
import { type SeriesRates, seriesRates } from "./flowMotion";

/** How far back the series is asked for, and how often: ten one-minute
 *  moments, refreshed every half minute, since the engine snapshots the
 *  counts once a minute per worker. */
const WINDOW_MS = 10 * 60_000;
const POINTS = 10;
const REFRESH_MS = 30_000;

/** The source read rate and the transform's drop rate, read off the
 *  engine's records series while the pipeline runs; undefined until the
 *  first answer, or when the engine has no series for the pipeline.
 *  `startedAt` names the run: a restarted pipeline gets a fresh query
 *  rather than the previous run's rates from the cache (the app keeps
 *  answers fresh for ten seconds, and the interval is thirty). */
export function useSeriesRates(
  jobName: string,
  running: boolean,
  startedAt: string | null | undefined,
): SeriesRates | undefined {
  const { data } = useQuery({
    queryKey: ["flow-series-rates", jobName, startedAt ?? null],
    queryFn: async ({ signal }) => {
      const to = new Date();
      const from = new Date(to.getTime() - WINDOW_MS);
      const doc = await reportsApi.recordsSeries(
        jobName,
        { from: from.toISOString(), to: to.toISOString(), points: POINTS },
        signal,
      );
      return seriesRates(doc);
    },
    enabled: running && !!jobName,
    refetchInterval: REFRESH_MS,
    retry: false,
  });
  return running ? data : undefined;
}
